import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./edit-contract.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import Checkbox from "../../../../../ui/Checkbox/Checkbox";
import { useEditContractMutation, useGetContractQuery } from "../contractApi";
import { useGetAllContractorsQuery } from "../../contractors/contractorsApi";

type Props = {
  id: number;
  close: () => void;
};

type State = {
  errors?: {
    id?: string[];
    name?: string[];
    date_start?: string[];
    date_end?: string[];
    contractor_id?: string[];
    total_cost?: string[];
    comment?: string[];
    active?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  name: z.string().min(1, {
    message: "Введите название",
  }),
  date_start: z.string().date("Введите дату договора"),
  date_end: z.string().date("Введите дату окончания контракта"),
  contractor_id: z.coerce.number({
    message: "Выберите значение из списка",
  }),
  comment: z.string(),
  total_cost: z.coerce.number({
    message: "Значение должно быть числом",
  }),
  active: z.coerce.boolean(),
});

const Form: FC<Props> = ({ close, id }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isActive, setIsActive] = useState(false);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [edit, { isError, isLoading, isSuccess }] = useEditContractMutation();
  const {
    data: contractors,
    isFetching: isContractorsFetching,
    isError: isContractorsError,
  } = useGetAllContractorsQuery();

  const {
    data: contract,
    isFetching: isContractFetching,
    isError: isContractError,
  } = useGetContractQuery(id);

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      name: formData.get("name"),
      date_start: formData.get("date_start"),
      date_end: formData.get("date_end"),
      contractor_id: formData.get("contractor_id"),
      comment: formData.get("comment"),
      total_cost: formData.get("total_cost"),
      active: formData.get("active"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const {
      name,
      date_start,
      date_end,
      contractor_id,
      comment,
      active,
      total_cost,
    } = validatedFields.data;

    edit({
      id,
      name,
      date_start,
      date_end,
      comment,
      active,
      contractor_id,
      total_cost,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  useEffect(() => {
    if (!contract) return;
    setIsActive(Boolean(contract.active));
  }, [contract]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Редактирование контракта</div>
      <div className={styles.row}>
        <label htmlFor="name">Название</label>
        <input
          className={styles.input}
          name="name"
          placeholder="Название"
          defaultValue={contract?.name}
        />
        <div id="name-error" aria-live="polite" aria-atomic="true">
          {state.errors?.name &&
            state.errors.name.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="date_start">Дата договора</label>
        <input
          className={styles.input}
          type="date"
          name="date_start"
          placeholder="Дата договора"
          defaultValue={contract?.date_start}
        />
        <div id="date_start-error" aria-live="polite" aria-atomic="true">
          {state.errors?.date_start &&
            state.errors.date_start.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="date_end">Дата окончания договора</label>
        <input
          className={styles.input}
          type="date"
          name="date_end"
          placeholder="Дата окончания договора"
          defaultValue={contract?.date_end}
        />
        <div id="date_end-error" aria-live="polite" aria-atomic="true">
          {state.errors?.date_end &&
            state.errors.date_end.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="contractor_id">Подрядчик</label>
        {isContractorsFetching ? (
          <div>Загрузка списка...</div>
        ) : (
          <select
            className={styles.select}
            name="contractor_id"
            defaultValue={contract?.contractor_id}
          >
            {contractors?.map((item) => (
              <option key={item.id} className={styles.option} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        )}

        <div id="contractor_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.contractor_id &&
            state.errors.contractor_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="total_cost">Общая стоимость</label>
        <input
          className={styles.input}
          name="total_cost"
          placeholder="Общая стоимость"
          defaultValue={contract?.total_cost}
        />
        <div id="total_cost-error" aria-live="polite" aria-atomic="true">
          {state.errors?.total_cost &&
            state.errors.total_cost.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="comment">Примечание</label>
        <textarea
          className={styles.input}
          name="comment"
          placeholder="Примечание"
          defaultValue={contract?.comment}
        />
        <div id="comment-error" aria-live="polite" aria-atomic="true">
          {state.errors?.comment &&
            state.errors.comment.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="active">Активность</label>
        <div className={styles.checkbox}>
          <Checkbox
            name="active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isContractorsError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
          autoHideDuration={3000}
        >
          Не удалось загрузить список подрядчиков.
        </Snackbar>
      )}
    </form>
  );
};

export const EditContract: FC<Props> = ({ close, id }) => {
  return (
    <Modal close={close}>
      <Form close={close} id={id} />
    </Modal>
  );
};
